/**
 * Portfolio Engagement Table Component
 * Shows one row per engagement with financial summary, sortable columns,
 * pagination (8 rows/page), at-risk row highlighting, and row click navigation.
 */

import { useMemo, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { EngagementTableRow } from '@/types/ui/dashboard'
import { cn } from '@/lib/utils'
import { useFormatters } from '@/hooks/use-formatters'
import { DataTable, type ColumnDef } from '@/components/ui/data-table'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { AppLink } from '@/components/ui/app-link'

const STATUS_COLORS: Record<string, string> = {
  Active: 'text-green-700 bg-green-50',
  AtRisk: 'text-red-700 bg-red-50',
  PendingChange: 'text-amber-700 bg-amber-50',
  Complete: 'text-slate-600 bg-slate-100',
  Draft: 'text-slate-500 bg-slate-50',
  PendingReview: 'text-blue-700 bg-blue-50',
}

interface PortfolioEngagementTableProps {
  data: EngagementTableRow[] | null
  isLoading?: boolean
  error?: string | null
  getStatusLabel?: (code: string) => string
  /**
   * Rows arrive pre-aggregated per change-request chain from the SP, so this only changes labelling
   * and expandability — the columns, sorting, footer totals and export need no special handling.
   */
  rollUp?: boolean
  /**
   * Supply to render the Roll-up toggle in this table's header. It lives here rather than in the
   * page filter bar because the Engagement Summary table is the only thing on the dashboard it
   * affects — the KPI cards and charts are unchanged either way.
   */
  onRollUpChange?: (value: boolean) => void
  /**
   * Fetches the same rows with roll-up off, for the exports. A rolled-up row is a sum, so the
   * members behind it are not in `data` at all — only their ids, via chainMembers. Rather than
   * widen that payload with a copy of every column for a feature only the export uses, the export
   * asks for the un-rolled set at the moment it needs it.
   */
  fetchUnrolledRows?: () => Promise<EngagementTableRow[]>
}

export function PortfolioEngagementTable({ data, isLoading, error, getStatusLabel, rollUp = false, onRollUpChange, fetchUnrolledRows }: PortfolioEngagementTableProps) {
  const resolveStatus = (code: string) => getStatusLabel?.(code) ?? code
  const { formatCurrency, formatDate } = useFormatters()
  const navigate = useNavigate()

  const totals = useMemo(() => {
    if (!data || data.length === 0) return null
    return {
      contractAmount: data.reduce((s, r) => s + r.contractAmount, 0),
      revenueRecognitionToDate: data.reduce((s, r) => s + r.revenueRecognitionToDate, 0),
      actualCostToDate: data.reduce((s, r) => s + r.actualCostToDate, 0),
      grossProfit: data.reduce((s, r) => s + r.grossProfit, 0),
      remainingContractAmount: data.reduce((s, r) => s + r.remainingContractAmount, 0),
    }
  }, [data])

  const columns: ColumnDef<EngagementTableRow>[] = [
    {
      key: 'engagementName',
      header: 'Engagement',
      sortable: true,
      sortKey: 'engagementName',
      // The chevron lives in this cell rather than in a dedicated expander column so the header
      // row, footerColSpan and every other consumer of DataTable stay untouched.
      render: (row, ctx) => (
        <div className="flex items-center gap-1.5">
          {ctx.canExpand ? (
            <button
              type="button"
              // The row itself navigates on click; this must not trigger that
              onClick={e => { e.stopPropagation(); ctx.toggleExpanded() }}
              onKeyDown={e => e.stopPropagation()}
              aria-expanded={ctx.isExpanded}
              aria-label={
                ctx.isExpanded
                  ? `Hide the ${row.chainMembers?.length ?? 0} engagements in ${row.engagementName}`
                  : `Show the ${row.chainMembers?.length ?? 0} engagements in ${row.engagementName}`
              }
              className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {ctx.isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            // Keeps names aligned whether or not a row has a chevron
            <span className="w-5 shrink-0" aria-hidden="true" />
          )}
          {/* A link rather than plain text so its context menu can open the engagement in a new tab */}
          <AppLink
            to={`/${row.id}`}
            // The row also navigates on Enter; letting the key bubble would navigate twice
            onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
            title={row.engagementName}
            className="block max-w-[200px] truncate text-left font-medium hover:underline hover:text-blue-700 focus-visible:outline-none focus-visible:underline"
          >
            {row.engagementName}
          </AppLink>
          {ctx.canExpand && (
            <span className="shrink-0 rounded-full bg-muted px-1.5 text-[11px] leading-4 text-muted-foreground">
              {row.chainMembers?.length}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      sortable: true,
      sortKey: 'client',
      render: row => (
        <span className="text-muted-foreground whitespace-nowrap max-w-[160px] truncate block" title={row.client}>
          {row.client}
        </span>
      ),
    },
    {
      key: 'branch',
      header: 'Branch',
      sortable: true,
      sortKey: 'branch',
      render: row => <span className="text-muted-foreground whitespace-nowrap">{row.branch}</span>,
    },
    {
      key: 'engagementLead',
      header: 'Eng. Lead',
      sortable: true,
      sortKey: 'engagementLead',
      render: row => (
        <span className="text-muted-foreground whitespace-nowrap max-w-[140px] truncate block" title={row.engagementLead}>
          {row.engagementLead}
        </span>
      ),
    },
    {
      key: 'engagementStatus',
      header: 'Status',
      sortable: true,
      sortKey: 'engagementStatus',
      render: row => (
        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap', STATUS_COLORS[row.engagementStatus] ?? 'text-slate-600 bg-slate-100')}>
          {resolveStatus(row.engagementStatus)}
        </span>
      ),
    },
    {
      key: 'isPreEngagement',
      header: 'Pre-engagement',
      sortable: true,
      sortKey: 'isPreEngagement',
      render: row => (
        <span className="text-muted-foreground whitespace-nowrap">
          {row.isPreEngagement ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'billingType',
      header: 'Billing',
      sortable: true,
      sortKey: 'billingType',
      render: row => <span className="text-muted-foreground whitespace-nowrap">{row.billingType}</span>,
    },
    {
      key: 'contractAmount',
      header: 'Contract',
      align: 'right',
      sortable: true,
      sortKey: 'contractAmount',
      render: row => <span className="whitespace-nowrap">{formatCurrency(row.contractAmount)}</span>,
      footerRender: () => totals ? <span className="whitespace-nowrap">{formatCurrency(totals.contractAmount)}</span> : null,
    },
    {
      key: 'revenueRecognitionToDate',
      header: 'Revenue to Date',
      align: 'right',
      sortable: true,
      sortKey: 'revenueRecognitionToDate',
      render: row => <span className="whitespace-nowrap">{formatCurrency(row.revenueRecognitionToDate)}</span>,
      footerRender: () => totals ? <span className="whitespace-nowrap">{formatCurrency(totals.revenueRecognitionToDate)}</span> : null,
    },
    {
      key: 'actualCostToDate',
      header: 'Actual Cost',
      align: 'right',
      sortable: true,
      sortKey: 'actualCostToDate',
      render: row => <span className="whitespace-nowrap">{formatCurrency(row.actualCostToDate)}</span>,
      footerRender: () => totals ? <span className="whitespace-nowrap">{formatCurrency(totals.actualCostToDate)}</span> : null,
    },
    {
      key: 'grossProfit',
      header: 'Gross Profit',
      align: 'right',
      sortable: true,
      sortKey: 'grossProfit',
      render: row => (
        <span className={cn('whitespace-nowrap', row.grossProfit < 0 ? 'text-red-600' : '')}>
          {formatCurrency(row.grossProfit)}
        </span>
      ),
      footerRender: () => totals ? (
        <span className={cn('whitespace-nowrap', totals.grossProfit < 0 ? 'text-red-600' : '')}>
          {formatCurrency(totals.grossProfit)}
        </span>
      ) : null,
    },
    {
      key: 'remainingContractAmount',
      header: 'Remaining',
      align: 'right',
      sortable: true,
      sortKey: 'remainingContractAmount',
      render: row => <span className="whitespace-nowrap">{formatCurrency(row.remainingContractAmount)}</span>,
      footerRender: () => totals ? <span className="whitespace-nowrap">{formatCurrency(totals.remainingContractAmount)}</span> : null,
    },
  ]

  // The engagements a rolled-up row collapsed. Rendered as a nested panel rather than as extra table
  // rows so it cannot disturb the parent grid's column widths.
  // The one place a row becomes export columns. Parent and child rows both go through it, so a
  // column added here cannot end up on one and not the other.
  const toExportRow = (r: EngagementTableRow): Record<string, unknown> => ({
    'Engagement Name': r.engagementName,
    'Engagement Code': r.engagementCode,
    Client: r.client,
    Branch: r.branch,
    'Eng. Lead': r.engagementLead,
    Status: resolveStatus(r.engagementStatus),
    'Pre-engagement': r.isPreEngagement ? 'Yes' : 'No',
    'Billing Type': r.billingType,
    'Contract Amount': r.contractAmount,
    'Revenue to Date': r.revenueRecognitionToDate,
    'Actual Cost': r.actualCostToDate,
    'Gross Profit': r.grossProfit,
    'Remaining Contract': r.remainingContractAmount,
    '% Actual to Projected': r.percentActualToProjected ?? '',
  })

  const buildRolledExcelRows = async () => {
    const unrolled = await fetchUnrolledRows!()
    const byId = new Map(unrolled.map(r => [String(r.id), r]))

    return (data ?? []).flatMap(row => {
      const parent = { data: toExportRow(row), level: 0 }
      const members = row.chainMembers ?? []
      // A group of one is the engagement itself: nesting it under a copy of itself would double
      // every figure in the file for no information.
      if (members.length < 2) return [parent]

      // A member missing from the un-rolled set cannot normally happen - both sides run the same
      // filters - but if it does, drop the whole group rather than nest a partial one. Children that
      // visibly fail to add up to the summary above them read as an error in the summary, which is
      // the one figure here that is definitely right.
      const children = members
        .map(m => byId.get(String(m.id)))
        .filter((r): r is EngagementTableRow => !!r)
        .map(r => ({ data: toExportRow(r), level: 1 }))

      return children.length === members.length ? [parent, ...children] : [parent]
    })
  }

  // Exactly what the un-rolled screen shows: the API applies the same filters either way, so this
  // needs no reassembly from the chains.
  const buildUnrolledCsvRows = async () => (await fetchUnrolledRows!()).map(toExportRow)

  const renderChainMembers = (row: EngagementTableRow) => {
    const members = row.chainMembers ?? []
    return (
      <div className="bg-slate-100 px-4 py-3">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          {members.length} engagement{members.length !== 1 ? 's' : ''} in this roll-up
        </p>
        <table className="w-full">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="py-1 pr-3 text-left font-semibold">Engagement</th>
              <th className="py-1 pr-3 text-left font-semibold">Code</th>
              <th className="py-1 pr-3 text-left font-semibold">Status</th>
              <th className="py-1 pr-3 text-left font-semibold">Start</th>
              <th className="py-1 pr-3 text-left font-semibold">End</th>
              <th className="py-1 pr-3 text-right font-semibold">Contract</th>
              <th className="py-1 pr-3 text-right font-semibold">Billed</th>
              <th className="py-1 text-right font-semibold">Actual Cost</th>
            </tr>
          </thead>
          <tbody>
            {members.map(m => (
              <tr
                key={m.id}
                onClick={() => navigate(`/${m.id}`)}
                tabIndex={0}
                role="link"
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    navigate(`/${m.id}`)
                  }
                }}
                className="cursor-pointer border-t border-slate-200 text-sm hover:bg-slate-200/60"
              >
                {/* Indented to line up with the parent row's name, which sits after the cell padding, chevron and gap */}
                <td className="py-1.5 pr-3 pl-[22px]" title={m.name ?? undefined}>
                  <div className="flex items-center">
                    {/* A link rather than plain text so its context menu can open the engagement in a new tab */}
                    <AppLink
                      to={`/${m.id}`}
                      // The row also navigates on Enter; letting the key bubble would navigate twice
                      onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
                      className="block max-w-[220px] truncate text-left text-sm font-medium hover:underline hover:text-blue-700 focus-visible:outline-none focus-visible:underline"
                    >
                      {m.name}
                    </AppLink>
                    {/* The engagement the collapsed row is named after */}
                    {m.isRoot && (
                      <span className="ml-1.5 shrink-0 rounded bg-slate-300/70 px-1 text-[10px] uppercase tracking-wide text-slate-700">
                        Original
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-1.5 pr-3 whitespace-nowrap">{m.code}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap">
                  <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_COLORS[m.status ?? ''] ?? 'text-slate-600 bg-slate-100')}>
                    {resolveStatus(m.status ?? '')}
                  </span>
                </td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-muted-foreground">{formatDate(m.startDate)}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-muted-foreground">{formatDate(m.endDate)}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-right tabular-nums">{formatCurrency(m.contractAmount)}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-right tabular-nums">{formatCurrency(m.billedToDate)}</td>
                <td className="py-1.5 whitespace-nowrap text-right tabular-nums">{formatCurrency(m.actualCostToDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <DataTable<EngagementTableRow>
      id="portfolio-engagement-table"
      title="Engagement Summary"
      // "Financial summary", not "Financial rollup" — roll-up now names a specific feature on this
      // table, so calling the ungrouped view a rollup describes it as the thing it is not.
      description={
        rollUp
          ? data
            ? `Financial summary roll-up — ${data.length} roll-up${data.length !== 1 ? 's' : ''}`
            : 'Financial summary roll-up'
          : data
            ? `Financial summary per engagement — ${data.length} engagement${data.length !== 1 ? 's' : ''}`
            : 'Financial summary per engagement'
      }
      columns={columns}
      data={data}
      getRowKey={row => row.id}
      isLoading={isLoading}
      error={error}
      emptyMessage="No engagement data available"
      onRowClick={row => navigate(`/${row.id}`)}
      rowClassName={row =>
        row.engagementStatus === 'AtRisk'
          ? 'bg-red-50/60 hover:bg-red-100/60'
          : 'hover:bg-muted/30'
      }
      showFooter={!!totals}
      footerColSpan={7}
      headerActions={
        onRollUpChange && (
          <div className="flex items-center gap-2">
            <Label
              htmlFor="engagement-summary-rollup"
              className="text-xs text-muted-foreground whitespace-nowrap cursor-pointer"
            >
              Roll-up engagements
            </Label>
            <Switch
              id="engagement-summary-rollup"
              checked={rollUp}
              onCheckedChange={onRollUpChange}
              aria-label="Group related engagements into a single row"
            />
          </div>
        )
      }
      expandedRowRender={renderChainMembers}
      // Only a genuine chain has anything to decompose — a single engagement rolled up with itself
      // would open a panel restating the row above it.
      isRowExpandable={row => (row.chainMembers?.length ?? 0) > 1}
      exportConfig={{
        // Distinguish the two Excel exports: the same filters produce different row counts and totals
        filenamePrefix: rollUp ? 'PortfolioEngagementRollups' : 'PortfolioEngagements',
        // CSV holds the un-rolled detail whatever the toggle says, so it is named for what is in it.
        // Naming it for the toggle would put two identical files in Downloads under different names.
        csvFilenamePrefix: 'PortfolioEngagements',
        sheetName: 'Engagements',
        buildExportData: () => (data ?? []).map(toExportRow),
        // Excel keeps the roll-up and makes it openable: a summary row carrying the chain's totals,
        // with the engagements behind it as collapsed children. Children use these same columns
        // rather than the expansion panel's, so every figure sits under the heading it belongs to
        // and the summary reads as the sum of the rows beneath it.
        buildExcelRows: rollUp && fetchUnrolledRows ? buildRolledExcelRows : undefined,
        // CSV has no grouping, so a summary row would be a total with no way to see what it totals.
        // Export the detail instead - the same file the user would get with roll-up off.
        buildCsvRows: rollUp && fetchUnrolledRows ? buildUnrolledCsvRows : undefined,
      }}
    />
  )
}
